import { useMutation, useQueryClient } from 'react-query';
import { PhoneMediaApi } from '@frontend/api-phone-media';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';

export const useAudioMutations = ({
  onSuccess,
  onClose,
  successMessage,
}: {
  onSuccess?: (uploadedId: string) => void;
  onClose?: () => void;
  successMessage?: string;
}) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const locationId = settingsTenantLocation?.id ?? '';
  const queryClient = useQueryClient();
  const baseUrl = '/phone/audio-library/v1/tts';

  // Upload audio mutation
  const uploadMutation = useMutation(
    ({ file, fileName }: { file: File; fileName: string }) => {
      return PhoneMediaApi.uploadAudio(file, fileName, { tenantId });
    },
    {
      onSuccess: async (data) => {
        await new Promise<void>((res) =>
          setTimeout(() => {
            queryClient.invalidateQueries({
              predicate: (query) => {
                const key = queryKeys.audioPicker().at(-2) as string;
                return query.queryKey.includes(key);
              },
            });
            queryClient.invalidateQueries([tenantId, ...queryKeys.audioPicker()]);
            queryClient.invalidateQueries([locationId, ...queryKeys.phoneMedia()]);
            res();
            if (onSuccess && data?.audioFile.media_item_id) {
              onSuccess(data.audioFile.media_item_id);
            }
          }, 1000)
        );
        alerts.success(successMessage || t('Successfully uploaded audio file.'));
      },
      onError: () => {
        alerts.error(t('Failed to upload audio file.'));
      },
      onSettled: async () => {
        onClose?.();
      },
    }
  );

  // Generate TTS audio mutation
  const generateMutation = useMutation({
    mutationFn: ({
      name,
      message,
      language,
      voice,
      format = 'wav',
      input = 'ssml',
      silence = '1000ms',
    }: {
      name: string;
      message: string;
      language: string;
      voice: string;
      format?: string;
      input?: string;
      silence?: string;
    }) => {
      return http.post<any>(
        `${baseUrl}/generate/${encodeURIComponent(name)}`,
        {
          text: message,
          format,
          lang: language,
          voice,
          input,
          silence,
        },
        { responseType: 'blob' }
      );
    },
  });

  return {
    uploadMutation,
    generateMutation,
  };
};
